import React, { useState, useRef, useCallback, useEffect } from "react";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";

import "./ZoomableImage.scss";

const ZoomableImage = ({
  src,
  alt,
  label,
  canRotate = true,
  onLoad,
  onError,
  className = "",
  containerClassName = "",
  errorImage,
  errorText = "No image found",
}) => {
  const [rotation, setRotation] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isMouseOverContainer, setIsMouseOverContainer] = useState(false);
  const [dragData, setDragData] = useState({ x: 0, y: 0, dx: 0, dy: 0 });
  const [matrixData, setMatrixData] = useState([1, 0, 0, 1, 0, 0]);

  const sourceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const newMatrix = [zoom, 0, 0, zoom, panX, panY];
    setMatrixData(newMatrix);
  }, [zoom, panX, panY]);

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
    if (onLoad) onLoad();
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
    if (onError) onError();
  };

  const handleRotate = useCallback(() => {
    if (!canRotate) return;
    setRotation((prev) => prev + 90);
    setPanX(0);
    setPanY(0);
  }, [canRotate]);

  const handleRotateCounterClockwise = useCallback(() => {
    if (!canRotate) return;
    setRotation((prev) => prev - 90);
    setPanX(0);
    setPanY(0);
  }, [canRotate]);

  const handleZoomChange = useCallback((newZoom) => {
    const clampedZoom = Math.max(0.5, Math.min(5, newZoom));
    setZoom(clampedZoom);
    if (clampedZoom <= 1) {
      setPanX(0);
      setPanY(0);
    }
  }, []);

  const resetZoom = useCallback(() => {
    setZoom(1);
    setPanX(0);
    setPanY(0);
    setRotation((prev) => (prev % 360 === 0 ? prev : 0));
  }, []);

  const panStart = useCallback(
    (pageX, pageY, event) => {
      if (zoom <= 1) return;

      const newDragData = {
        dx: panX,
        dy: panY,
        x: pageX,
        y: pageY,
      };

      setDragData(newDragData);
      setIsDragging(true);

      if (containerRef.current) {
        containerRef.current.style.cursor = "grabbing";
      }

      event.stopPropagation();
      event.preventDefault();
    },
    [zoom, panX, panY],
  );

  const updatePanPosition = useCallback(
    (pageX, pageY) => {
      if (!isDragging) return;

      const deltaX = dragData.x - pageX;
      const deltaY = dragData.y - pageY;

      const newPanX = dragData.dx - deltaX;
      const newPanY = dragData.dy - deltaY;

      setPanX(newPanX);
      setPanY(newPanY);
    },
    [isDragging, dragData],
  );

  const panEnd = useCallback(() => {
    setIsDragging(false);
    if (containerRef.current) {
      containerRef.current.style.cursor = zoom > 1 ? "grab" : "default";
    }
  }, [zoom]);

  const handleMouseDown = useCallback(
    (e) => {
      panStart(e.pageX, e.pageY, e);
    },
    [panStart],
  );

  const handleMouseMove = useCallback(
    (e) => {
      updatePanPosition(e.pageX, e.pageY);
    },
    [updatePanPosition],
  );

  const handleMouseUp = useCallback(() => {
    panEnd();
  }, [panEnd]);

  const handleDoubleClick = useCallback(() => {
    if (zoom === 1) {
      handleZoomChange(2);
    } else {
      resetZoom();
    }
  }, [zoom, handleZoomChange, resetZoom]);

  const handleMouseEnter = useCallback(() => {
    setIsMouseOverContainer(true);
  }, []);

  const handleMouseLeaveContainer = useCallback(() => {
    setIsMouseOverContainer(false);
    setIsDragging(false);
    if (containerRef.current) {
      containerRef.current.style.cursor = zoom > 1 ? "grab" : "default";
    }
  }, [zoom]);

  const handleTouchStart = useCallback(
    (e) => {
      const touch = e.touches[0];
      panStart(touch.pageX, touch.pageY, e);
    },
    [panStart],
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        updatePanPosition(touch.pageX, touch.pageY);
      }
    },
    [updatePanPosition],
  );

  const handleTouchEnd = useCallback(() => {
    panEnd();
  }, [panEnd]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const wheelHandler = (e) => {
        if (isMouseOverContainer) {
          e.preventDefault();
          e.stopPropagation();
          const delta = e.deltaY;
          const zoomStep = 0.1;
          const newZoom = delta > 0 ? zoom - zoomStep : zoom + zoomStep;
          handleZoomChange(newZoom);
        }
      };

      container.addEventListener("wheel", wheelHandler, { passive: false });

      return () => {
        container.removeEventListener("wheel", wheelHandler);
      };
    }
  }, [isMouseOverContainer, zoom, handleZoomChange]);

  const imageTransformStyle = {
    transform: `rotate(${rotation}deg) matrix(${matrixData.toString()})`,
    transition: isDragging ? "none" : "transform 0.3s ease",
    cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default",
    userSelect: "none",
    pointerEvents: "none",
  };

  if (!src || hasError) {
    return (
      <div className={`zoomable-image-item ${containerClassName}`}>
        <div className="zoomable-image-container">
          <div className="zoomable-image-placeholder">
            {errorImage && (
              <img
                src={errorImage}
                alt="not found"
                className="zoomable-error-image"
              />
            )}
            <p className="zoomable-error-text">{errorText}</p>
          </div>
        </div>
        {label && <div className="zoomable-image-label">{label}</div>}
      </div>
    );
  }

  return (
    <div className={`zoomable-image-item ${containerClassName}`}>
      <div
        className="zoomable-image-container"
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeaveContainer}
        onMouseEnter={handleMouseEnter}
        onDoubleClick={handleDoubleClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default",
          userSelect: "none",
        }}
      >
        {isLoading && (
          <div className="zoomable-image-loading">
            <div className="zoomable-loading-spinner"></div>
          </div>
        )}

        <img
          ref={sourceRef}
          src={src}
          alt={alt}
          className={`zoomable-image ${className}`}
          style={imageTransformStyle}
          onLoad={handleImageLoad}
          onError={handleImageError}
          draggable={false}
        />
      </div>

      <div className="zoomable-controls">
        <div className="zoom-slider-section">
          <input
            type="range"
            min="0.5"
            max="5"
            step="0.1"
            value={zoom}
            onChange={(e) => handleZoomChange(parseFloat(e.target.value))}
            className="zoom-slider"
          />
        </div>
        <div className="zoom-info-section">
          <span className="zoom-label">Zoom: {Math.round(zoom * 100)}%</span>
          <div className="control-buttons">
            <button
              className="reset-zoom-btn"
              onClick={resetZoom}
              title="Reset zoom and rotation"
            >
              Reset
            </button>
            <button
              className="rotate-btn rotate-btn-left"
              onClick={handleRotateCounterClockwise}
              disabled={!canRotate}
              title="Rotate counterclockwise"
            >
              <RotateLeftIcon />
            </button>
            <button
              className="rotate-btn rotate-btn-right"
              onClick={handleRotate}
              disabled={!canRotate}
              title="Rotate clockwise"
            >
              <RotateRightIcon />
            </button>
          </div>
        </div>
      </div>

      {label && <div className="zoomable-image-label">{label}</div>}
    </div>
  );
};

export default ZoomableImage;
